/**
 * The design-system flows' real dependencies in the editor: this device's
 * OPFS for the site, builder-api (through the BFF) for the authorized
 * pointer and the blobs, the esbuild-wasm worker, the sandboxed iframe.
 */
import { OpfsBlobStore } from "@website-builder/blob-store/opfs";

import { compileInWorker } from "../dsSandbox/compileHost.js";
import { createDsSandbox } from "../dsSandbox/sandboxHost.js";
import type { DsDeps, DsServer } from "./designSystemFlows.js";

/** Why the server said no, readably (403 is "only the owner can"). */
async function failure(r: Response, what: string): Promise<Error> {
  if (r.status === 403) return new Error(`only the site's owner can ${what}`);
  const body = (await r.json().catch(() => null)) as {
    message?: string;
  } | null;
  return new Error(
    `couldn't ${what} (HTTP ${r.status}${body?.message ? `: ${body.message}` : ""})`,
  );
}

export function httpDsServer(siteId: string): DsServer {
  const site = `/api/sites/${encodeURIComponent(siteId)}`;
  return {
    async getActive() {
      const r = await fetch(`${site}/design-system`, {
        credentials: "same-origin",
      });
      if (!r.ok) throw await failure(r, "read the design system");
      const { manifestSha } = (await r.json()) as {
        manifestSha: string | null;
      };
      return manifestSha;
    },
    async setActive(manifestSha) {
      const r = await fetch(`${site}/design-system`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ manifestSha }),
      });
      if (!r.ok) throw await failure(r, "change the design system");
    },
    async upload(sha, bytes, contentType) {
      const r = await fetch(`${site}/assets`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": contentType, "X-Asset-Sha256": sha },
        body: new Blob([bytes as unknown as ArrayBuffer]),
      });
      if (!r.ok) throw await failure(r, "upload the design system");
    },
    async fetch(sha) {
      const r = await fetch(`${site}/blob/${sha}`, {
        credentials: "same-origin",
      });
      if (r.status === 404) return null;
      if (!r.ok) throw await failure(r, "fetch the design system");
      return new Uint8Array(await r.arrayBuffer());
    },
  };
}

export function browserDsDeps(siteId: string): DsDeps {
  return {
    local: new OpfsBlobStore(["sites", siteId]),
    server: httpDsServer(siteId),
    compile: compileInWorker,
    createSandbox: () => createDsSandbox(),
  };
}
